import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  NotificationDeliveryPreferencesSchema,
  type NotificationDeliveryPreferencesJSON,
} from "notifications/delivery-preferences/_schemas/notification-delivery-preferences-schema";
import { onPrincipalChanged } from "users/__session/principal-change";
import Session from "users/__session/session";
import { serializeQueryArgsBySessionCredential } from "users/__session/session-query-cache-keys";

const PREFERENCES_URL = "users/me/notification-preferences";

// One page holds one person's choices, so one queue serves every save.
let saveQueue: Promise<void> = Promise.resolve();
let latestSave = 0;
let pendingSaves = 0;
let saveFailedSinceDrain = false;

// A change of principal starts a new generation. A save queued in an earlier one is refused when
// its turn comes, so it can never be sent for the person signed in after it (PR #6156 review).
let principalGeneration = 0;
onPrincipalChanged(() => {
  principalGeneration++;
  saveQueue = Promise.resolve();
});

/** Who a save was made for: the user and the token they had when they made it. */
function principalOf(session: Session | null | undefined): string | undefined {
  const userId = session?.user?.id?.value;
  const token = session?.authToken?.value;
  return userId && token ? `${userId} ${token}` : undefined;
}

export const PRINCIPAL_CHANGED_ERROR = {
  status: "CUSTOM_ERROR",
  error: "The signed-in user changed before this notification choice was saved.",
} as const;

/** How the signed-in person's notifications reach them (#6152). */
export const notificationDeliveryPreferencesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getNotificationDeliveryPreferences: build.query<NotificationDeliveryPreferencesJSON, { session: Session }>({
      query: ({ session }) => ({
        url: PREFERENCES_URL,
        headers: { Authorization: `Bearer ${session.authToken?.value}` },
      }),
      serializeQueryArgs: serializeQueryArgsBySessionCredential,
      rawResponseSchema: NotificationDeliveryPreferencesSchema,
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "NotificationDeliveryPreferences", id: "ME" }],
    }),
    saveNotificationDeliveryPreferences: build.mutation<
      NotificationDeliveryPreferencesJSON,
      { session: Session; preferences: NotificationDeliveryPreferencesJSON }
    >({
      // Saves go one at a time, in the order the person made them. Two PUTs in flight together
      // can reach the server in either order, and the server keeps whichever lands last
      // (PR #6156 review).
      //
      // Each save is bound to the principal that made it, and is sent with that principal's token.
      // When its turn comes, it is refused, unsent, if the signed-in principal is now a different
      // one: an impersonation started while saves waited would otherwise send them for the other
      // person, because the shared base query reads the token that is stored now.
      async queryFn({ session, preferences }, _api, _extraOptions, baseQuery) {
        const madeBy = principalOf(session);
        const generation = principalGeneration;
        const previous = saveQueue;
        let release: () => void = () => undefined;
        saveQueue = new Promise<void>((resolve) => (release = resolve));
        await previous;
        const signedIn = principalOf(Session.loadFromStorage(() => {}));
        if (!madeBy || generation !== principalGeneration || signedIn !== madeBy) {
          release();
          return { error: PRINCIPAL_CHANGED_ERROR };
        }
        const result = await baseQuery({
          url: PREFERENCES_URL,
          method: "PUT",
          body: preferences,
          headers: { Authorization: `Bearer ${session.authToken?.value}` },
        });
        release();
        if (result.error) return { error: result.error };
        return { data: result.data as NotificationDeliveryPreferencesJSON };
      },
      responseSchema: NotificationDeliveryPreferencesSchema,
      onSchemaFailure: logSchemaFailure,
      // The choice shows at once. Only the answer to the newest save may replace what the page
      // shows: an older answer, or an older failure, would put back a choice the person has since
      // changed. When the last save settles after any failure, the stored choices are read again.
      async onQueryStarted({ session, preferences }, { dispatch, queryFulfilled }) {
        const save = ++latestSave;
        pendingSaves++;
        dispatch(
          notificationDeliveryPreferencesApi.util.updateQueryData(
            "getNotificationDeliveryPreferences",
            { session },
            () => preferences,
          ),
        );
        const settled = await queryFulfilled.then(
          ({ data }) => ({ data }),
          () => ({ data: undefined }),
        );
        pendingSaves--;
        if (!settled.data) saveFailedSinceDrain = true;
        if (settled.data && save === latestSave) {
          const data = settled.data;
          dispatch(
            notificationDeliveryPreferencesApi.util.updateQueryData(
              "getNotificationDeliveryPreferences",
              { session },
              () => data,
            ),
          );
        }
        if (pendingSaves === 0 && saveFailedSinceDrain) {
          saveFailedSinceDrain = false;
          dispatch(notificationDeliveryPreferencesApi.util.invalidateTags([{ type: "NotificationDeliveryPreferences", id: "ME" }]));
        }
      },
    }),
  }),
});

export const {
  useGetNotificationDeliveryPreferencesQuery,
  useSaveNotificationDeliveryPreferencesMutation,
} = notificationDeliveryPreferencesApi;
